import type { ResolvedFigure, ShapeRole, StackFrame, StackFramesSpec } from '../model.js';
import { box, finishFigure, point, roundGeometry, SceneBuilder, validateViewport } from '../scene-builder.js';

export function stackFrameId(frameId: string): string {
  return `frame.${frameId}`;
}

export function stackLocalId(frameId: string, localId: string): string {
  return `${stackFrameId(frameId)}.local.${localId}`;
}

/**
 * Compiles semantic call frames into exact isometric slabs. Array order is call
 * depth: the first frame owns depth 0 and each following frame was called by it.
 * The fixed projection is explanatory—the top and side faces expose stacking—
 * while no authored coordinate or arbitrary perspective enters the figure spec.
 */
export function compileStackFrames(spec: StackFramesSpec): ResolvedFigure {
  validateStackFrames(spec);

  const builder = new SceneBuilder();
  const { width } = spec.viewport;
  const narrow = width <= 390;
  const count = spec.frames.length;
  const sideMargin = width <= 340 ? 20 : width <= 390 ? 26 : 48;
  const growthRail = narrow ? 24 : 34;
  const isoX = narrow ? 15 : 22;
  const isoY = narrow ? 10 : 14;
  const frontHeight = narrow ? 62 : 68;
  const frameGap = narrow ? 9 : 12;
  const pitch = isoY + frontHeight + frameGap;
  const availableTop = narrow ? 59 : 65;
  // An invariant origin means push/pop transitions add or ghost one slab without
  // moving any surviving caller frame.
  const stackTop = roundGeometry(availableTop);
  const bodyX = sideMargin + growthRail;
  const bodyWidth = width - bodyX - sideMargin - isoX;
  const warnings: string[] = [];

  builder.text('stack.eyebrow', 'stack', 'annotation', point(sideMargin, 24), 'ISOMETRIC CALL STACK · DEPTH IS STRUCTURE', {
    size: narrow ? 8.5 : 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });

  const railX = sideMargin + (narrow ? 7 : 11);
  const railStart = stackTop + isoY - 4;
  const railEnd = stackTop + pitch * (count - 1) + isoY + frontHeight - 2;
  builder.line('stack.growth.arrow', 'stack.growth', 'pointer', point(railX, railStart), point(railX, railEnd), { markerEnd: true });
  builder.text('stack.growth.label', 'stack.growth', 'annotation', point(railX - (narrow ? 8 : 11), (railStart + railEnd) / 2), 'STACK GROWS DOWN', {
    size: narrow ? 7.5 : 9,
    mono: true,
    weight: 700,
    align: 'middle',
    uppercase: true,
    rotate: 90,
  });
  builder.anchor('stack.growth.start', 'stack.growth', point(railX, railStart), 'up');
  builder.anchor('stack.growth.end', 'stack.growth', point(railX, railEnd), 'down');

  spec.frames.forEach((frame, depth) => {
    const y = stackTop + pitch * depth;
    drawFrame(builder, spec, frame, depth, bodyX, y, bodyWidth, frontHeight, isoX, isoY, warnings);
  });

  const activeFrame = spec.frames.find((frame) => frame.status === 'active')!;
  builder.text('stack.pointer-label', 'stack.pointer', 'annotation', point(bodyX + bodyWidth + isoX, railEnd + 23), `SP · DEPTH ${spec.frames.indexOf(activeFrame) + 1}/${count}`, {
    size: narrow ? 8.5 : 10,
    mono: true,
    weight: 700,
    align: 'end',
    uppercase: true,
  });
  builder.relation('stack.pointer.points-to', 'points-to-active-frame', 'stack.pointer', stackFrameId(activeFrame.id), `SP points to ${activeFrame.functionName}`);

  const focusSemanticId = spec.focusFrameId ? stackFrameId(spec.focusFrameId) : stackFrameId(activeFrame.id);
  const active = [focusSemanticId, ...(spec.emphasis ?? [])];
  const returnedCount = spec.frames.filter((frame) => frame.status === 'returned').length;

  if (bodyWidth < 245) warnings.push('Frame faces use the narrow isometric label policy.');
  if (spec.frames.some((frame) => frame.locals.length === 4)) warnings.push('Four-local frames use compact value cells.');

  return finishFigure(spec, builder, {
    title: 'Each call pushes one frame onto the stack',
    subtitle: 'Isometric depth carries one fact only: nested calls occupy ordered stack frames until they return.',
    state: `depth:${count} · active:${activeFrame.id}${returnedCount ? ` · returned:${returnedCount}` : ''}${spec.focusFrameId ? ` · focus:${spec.focusFrameId}` : ''}`,
    active,
    warnings: unique(warnings),
  });
}

export function validateStackFrames(spec: StackFramesSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.growth !== 'down') throw new Error('stack-frames: the Foundation v2 view supports downward-growing stacks only');
  if (!spec.provenance.trim()) throw new Error('stack-frames: provenance is required');
  if (spec.frames.length < 2 || spec.frames.length > 5) throw new Error('stack-frames: require 2…5 frames');

  assertUnique(spec.frames.map((frame) => frame.id), 'frame');
  let activeCount = 0;
  let sawActive = false;
  let sawReturned = false;

  for (const frame of spec.frames) {
    if (!validId(frame.id)) throw new Error(`stack-frames: invalid frame id ${frame.id}`);
    if (!frame.functionName.trim()) throw new Error(`stack-frames: frame ${frame.id} requires a function name`);
    if (frame.locals.length < 1 || frame.locals.length > 4) throw new Error(`stack-frames: frame ${frame.id} requires 1…4 locals`);
    assertUnique(frame.locals.map((local) => local.id), `local in frame ${frame.id}`);

    if (frame.status === 'active') {
      activeCount += 1;
      sawActive = true;
      if (sawReturned) throw new Error('stack-frames: active frame cannot follow a returned frame');
    } else if (frame.status === 'suspended') {
      if (sawActive || sawReturned) throw new Error('stack-frames: suspended frames must precede the active frame');
    } else {
      if (!sawActive) throw new Error('stack-frames: returned frames must follow the active frame');
      sawReturned = true;
    }

    for (const local of frame.locals) {
      if (!validId(local.id)) throw new Error(`stack-frames: invalid local id ${local.id}`);
      if (!local.name.trim()) throw new Error(`stack-frames: local ${frame.id}.${local.id} requires a name`);
      if (typeof local.value === 'string' && !local.value.trim()) throw new Error(`stack-frames: local ${frame.id}.${local.id} requires a value`);
      if (!Number.isInteger(local.bytes) || local.bytes <= 0) throw new Error(`stack-frames: local ${frame.id}.${local.id} bytes must be a positive integer`);
    }
  }

  if (activeCount !== 1) throw new Error('stack-frames: exactly one active frame is required');
  if (spec.focusFrameId && !spec.frames.some((frame) => frame.id === spec.focusFrameId)) {
    throw new Error(`stack-frames: focus frame ${spec.focusFrameId} does not exist`);
  }
}

function drawFrame(
  builder: SceneBuilder,
  spec: StackFramesSpec,
  frame: StackFrame,
  depth: number,
  x: number,
  y: number,
  width: number,
  frontHeight: number,
  isoX: number,
  isoY: number,
  warnings: string[],
): void {
  const narrow = spec.viewport.width <= 390;
  const semanticId = stackFrameId(frame.id);
  const role = roleForStatus(frame.status);
  const top = [point(x, y + isoY), point(x + isoX, y), point(x + width + isoX, y), point(x + width, y + isoY)];
  const front = [point(x, y + isoY), point(x + width, y + isoY), point(x + width, y + isoY + frontHeight), point(x, y + isoY + frontHeight)];
  const side = [point(x + width, y + isoY), point(x + width + isoX, y), point(x + width + isoX, y + frontHeight), point(x + width, y + isoY + frontHeight)];
  const bounds = box(x, y, width + isoX, frontHeight + isoY);
  const statusWidth = narrow ? 53 : 66;
  const functionBudget = Math.max(8, Math.floor((width - statusWidth - 34) / (narrow ? 5.4 : 6.2)));
  const functionLabel = compactLabel(frame.functionName, functionBudget);

  builder.polygon(`${semanticId}.top`, semanticId, role, top, `${frame.functionName}, ${frame.status}, call depth ${depth + 1}`);
  builder.polygon(`${semanticId}.front`, semanticId, frame.status === 'returned' ? 'construction' : 'surface', front);
  builder.polygon(`${semanticId}.side`, semanticId, frame.status === 'returned' ? 'construction' : 'depth', side);

  builder.text(`${semanticId}.depth`, semanticId, 'annotation', point(x + 10, y + isoY + 18), `#${depth}`, {
    size: narrow ? 8 : 9,
    mono: true,
    weight: 700,
  });
  builder.text(`${semanticId}.function`, semanticId, frame.status === 'returned' ? 'annotation' : 'structure', point(x + (narrow ? 31 : 36), y + isoY + 19), functionLabel, {
    size: narrow ? 9.5 : 11,
    mono: true,
    weight: 700,
  });
  builder.rect(`${semanticId}.status-chip`, semanticId, role, box(x + width - statusWidth - 8, y + isoY + 7, statusWidth, 19), {
    radius: 8,
    label: `${frame.status} frame`,
  });
  builder.text(`${semanticId}.status`, semanticId, 'annotation', point(x + width - statusWidth / 2 - 8, y + isoY + 20), frame.status.toUpperCase(), {
    size: narrow ? 7 : 8,
    mono: true,
    weight: 700,
    align: 'middle',
    uppercase: true,
  });

  const localGap = narrow ? 4 : 6;
  const localX = x + 9;
  const localY = y + isoY + 33;
  const localWidth = (width - 18 - localGap * (frame.locals.length - 1)) / frame.locals.length;
  const localHeight = frontHeight - 40;
  frame.locals.forEach((local, index) => {
    const localSemanticId = stackLocalId(frame.id, local.id);
    const cellX = localX + index * (localWidth + localGap);
    const display = compactLocal(`${local.name}=${String(local.value)}`, Math.max(5, Math.floor((localWidth - 8) / (narrow ? 4.9 : 5.5))));
    builder.rect(`${localSemanticId}.cell`, localSemanticId, frame.status === 'returned' ? 'construction' : 'record', box(cellX, localY, localWidth, localHeight), {
      radius: 3,
      label: `${local.name}, value ${String(local.value)}, ${local.bytes} bytes, in ${frame.functionName}`,
    });
    builder.text(`${localSemanticId}.value`, localSemanticId, frame.status === 'returned' ? 'annotation' : 'structure', point(cellX + localWidth / 2, localY + localHeight / 2 + 3), display, {
      size: narrow ? 7.5 : 8.5,
      mono: true,
      weight: 600,
      align: 'middle',
    });
    builder.anchor(`${localSemanticId}.center`, localSemanticId, point(cellX + localWidth / 2, localY + localHeight / 2));
    builder.relation(`${localSemanticId}.belongs-to`, 'local-of-frame', localSemanticId, semanticId, `${local.name} occupies ${local.bytes} bytes in ${frame.functionName}`);
    if (display !== `${local.name}=${String(local.value)}`) warnings.push(`Local compacted: ${frame.id}.${local.id}`);
  });

  builder.anchor(`${semanticId}.top`, semanticId, point(x + width / 2 + isoX / 2, y), 'up');
  builder.anchor(`${semanticId}.center`, semanticId, point(x + width / 2, y + isoY + frontHeight / 2));
  builder.anchor(`${semanticId}.base`, semanticId, point(x + width / 2, y + isoY + frontHeight), 'down');
  builder.anchor(`${semanticId}.return`, semanticId, point(x + width + isoX, y + frontHeight / 2), 'right');
  builder.hit(semanticId, bounds);

  if (depth > 0) {
    const caller = spec.frames[depth - 1]!;
    if (frame.status === 'returned') {
      builder.relation(`${semanticId}.returns-to`, 'returns-to', semanticId, stackFrameId(caller.id), `${frame.functionName} returns to ${caller.functionName}`);
    } else {
      builder.relation(`${semanticId}.called-by`, 'called-by', semanticId, stackFrameId(caller.id), `${caller.functionName} calls ${frame.functionName}`);
    }
  }

  if (functionLabel !== frame.functionName) warnings.push(`Function name compacted: ${frame.id}`);
}

function roleForStatus(status: StackFrame['status']): ShapeRole {
  if (status === 'active') return 'active';
  if (status === 'returned') return 'construction';
  return 'depth';
}

function compactLabel(value: string, max: number): string {
  if (value.length <= max) return value;
  const tail = value.includes('.') ? value.split('.').at(-1)! : value;
  if (tail.length <= max) return tail;
  const head = Math.max(2, Math.ceil((max - 1) * 0.58));
  return `${tail.slice(0, head)}…${tail.slice(-(max - head - 1))}`;
}

function compactLocal(value: string, max: number): string {
  if (value.length <= max) return value;
  const equals = value.indexOf('=');
  if (equals > 0) {
    const name = value.slice(0, equals);
    const rawValue = value.slice(equals + 1);
    const valueRoom = max - Math.min(name.length, Math.ceil(max * 0.48)) - 2;
    if (valueRoom >= 1) return `${name.slice(0, max - valueRoom - 2)}…=${rawValue.slice(-valueRoom)}`;
  }
  return compactLabel(value, max);
}

function validId(value: string): boolean {
  return /^[a-z][a-z0-9-]*$/.test(value);
}

function assertUnique(values: string[], kind: string): void {
  if (new Set(values).size !== values.length) throw new Error(`stack-frames: ${kind} IDs must be unique`);
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}
